import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { delay, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environment/environment';
import { v4 as uuidv4 } from 'uuid';
import { CA } from '../../entities/ca';
import {
  mockCertificationAuthorities,
  mockCryptoTokenList,
  mockProfileList,
} from './mock-data';
import { CreateCryptoTokenRequestDto } from './dto/create-crypto-token-request-dto';
import { CaRequestDto } from './dto/ca-request-dto';
import { CryptotokenDto } from './dto/cryptotoken-dto';
import {
  CryptotokenKeyDto,
  cryptotokenKeyDtoToCryptotokenKey,
} from './dto/cryptotoken-key-dto';
import { UpdateCaRequestDto } from './dto/update-ca-request-dto';
import { Profile } from '../../entities/profile';
import { Api, ApiConfig, EndpointConstructor } from './api.types';
import { CryptotokenSlotDto } from './dto/cryptotoken-slot-dto';
import { CryptotokenSlot } from '../../entities/cryptotoken-slot';
import { CryptotokenKey } from '../../entities/cryptotoken-key';
import { CreateCertificateProfileRequestDto } from './dto/create-certificate-profile-request-dto';
import { UpdateProfileRequestDto } from './dto/update-profile-request-dto';
import { GenerateKeyRequestDto } from './dto/generate-key-request-dto';
import { ConsoleStatus } from '../../entities/console-status';
import { ConsoleStateResponseDto } from './dto/console-state-response-dto';
import { Store } from '@ngrx/store';
import { UserSelector } from '../../store/user/user.feature';
import { CreateCaFullDto } from './dto/create-ca-full-dto';
import { CaRevokeReason } from '../../entities/ca-revoke-reason';
import { blobToBase64 } from '../../util/blob-to-base64';

const consoleStatusUrl = '/admin/state';

const apiConfig = (api: EndpointConstructor) => ({
  caList: api.getEndpoint<CA[]>('/admin/ca/list'),

  createCa: api.postTextEndpoint<CaRequestDto>('/admin/ca'),

  updateCa: api.putEndpointWithParams<
    { caName: string },
    UpdateCaRequestDto,
    CA
  >(({ caName }) => `/admin/ca/${caName}/update`),

  deleteCa: api.deleteTextEndpointWithParams<{ caName: string }>(
    ({ caName }) => `/admin/ca/${caName}`,
  ),

  renewCa: api.postTextEndpointWithParams<{ caName: string }, void>(
    ({ caName }) => `/admin/ca/${caName}/renew`,
  ),

  revokeCa: api.postTextEndpointWithParams<
    { caName: string },
    { reason: CaRevokeReason; revokeLeafCertificates: boolean }
  >(({ caName }) => `/admin/ca/${caName}/revoke`),

  generateCrl: api.postBlobEndpointWithParams<{ caName: string }, void>(
    ({ caName }) => `/crl/${caName}`,
  ),

  profileList: api.getEndpoint<Profile[]>('/admin/profile/list'),

  deleteProfile: api.deleteTextEndpointWithParams<{
    profileName: string;
  }>(({ profileName }) => `/admin/profile/${profileName}`),

  cryptotokenList: api.getEndpoint<CryptotokenDto[]>('/admin/cryptotoken/list'),

  newCryptoToken:
    api.postTextEndpoint<CreateCryptoTokenRequestDto>('/admin/cryptotoken'),

  cryptotokenKeys: api.getEndpointWithParams<
    { tokenName: string },
    CryptotokenKeyDto[]
  >(({ tokenName }) => `/admin/cryptotoken/${tokenName}/keys`),

  cryptotokenSlots: api.getEndpoint<CryptotokenSlotDto[]>(
    '/admin/cryptotoken/slots',
  ),

  deleteCryptotoken: api.deleteTextEndpointWithParams<{
    tokenName: string;
  }>(({ tokenName }) => `/admin/cryptotoken/${tokenName}`),

  deleteCryptotokenKey: api.deleteTextEndpointWithParams<{
    tokenName: string;
    alias: string;
  }>(({ tokenName, alias }) => `/admin/cryptotoken/${tokenName}/key/${alias}`),

  createProfile:
    api.postTextEndpoint<CreateCertificateProfileRequestDto>('/admin/profile'),

  updateProfile: api.putEndpointWithParams<
    { profileName: string },
    UpdateProfileRequestDto,
    Profile
  >(({ profileName }) => `/admin/profile/${profileName}/update`),

  activateToken: api.putTextEndpointWithParams<
    { tokenName: string },
    { password: string }
  >(({ tokenName }) => `/admin/cryptotoken/${tokenName}/activate`),

  deactivateToken: api.putTextEndpointWithParams<{ tokenName: string }, void>(
    ({ tokenName }) => `/admin/cryptotoken/${tokenName}/deactivate`,
  ),

  createCryptotokenKey: api.postTextEndpointWithParams<
    {
      tokenName: string;
    },
    GenerateKeyRequestDto
  >(({ tokenName }) => `/admin/cryptotoken/${tokenName}/key`),

  consoleStatus: api.getEndpoint<ConsoleStateResponseDto>(consoleStatusUrl),

  provisionOperationalCa: api.postTextEndpoint<CreateCaFullDto>(
    '/admin/ca/operational',
  ),

  requestCsr: api.postTextEndpointWithParams<{ caName: string }, void>(
    ({ caName }) => `/admin/ca/${caName}/csr`,
  ),

  importCertChain: api.postTextEndpointWithParams<
    { caName: string },
    { externalCaChain: string }
  >(({ caName }) => `/admin/ca/${caName}/cert-chain`),
});

const codeEndpoint = '/admin/code';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private useMockData = false;

  private jwtToken: string | null = null;
  private user = this.store.select(UserSelector.selectUser);
  private token = '';

  // uses changeable item from local storage, hence a getter
  private get generalHeaders() {
    return new HttpHeaders({
      // FIXME localStorage should be removed
      Authorization: `Bearer ${this.token || localStorage.getItem('encodeAzureID')}`,
      'x-xpki-tracking-id': uuidv4(),
      'x-xpki-source': 'xCA-UI',
    });
  }

  private readonly statusHeaders = new HttpHeaders({});

  private apiConfig: ApiConfig = {
    baseUrl: environment.apiUrl,
    headers: url =>
      url === consoleStatusUrl ? this.statusHeaders : this.generalHeaders,
  };

  private api = new Api(this.httpClient, this.apiConfig);

  private endpoints = apiConfig(this.api);
  private codeEndpoint = `${this.apiConfig.baseUrl}${codeEndpoint}`;

  getConsoleStatus(): Observable<ConsoleStatus> {
    if (this.useMockData) {
      return of(ConsoleStatus.UP).pipe(delay(1000));
    }
    return this.endpoints
      .consoleStatus()
      .pipe(
        map(response =>
          response.adminOperationsEnabled
            ? ConsoleStatus.UP
            : ConsoleStatus.DOWN,
        ),
      );
  }

  private mockCertificationAuthorities = mockCertificationAuthorities.map(
    (v, i) =>
      i > 2
        ? {
            ...v,
            caName: `${v.caName}_copy_${i - 2}`,
            expireTime: i % 2 ? v.expireTime : Date.now() - 1000000,
          }
        : v,
  );

  private mockProfileList = mockProfileList.map((v, i) =>
    i > 1
      ? {
          ...v,
          profileName: `${v.profileName}_copy_${i - 1}`,
        }
      : v,
  );

  private mockCryptoTokenList = mockCryptoTokenList.map(v => v);

  getCaList(): Observable<CA[]> {
    if (this.useMockData) {
      console.debug('getcalist', Date.now());
      return of(this.mockCertificationAuthorities).pipe(delay(1000));
    }
    return this.endpoints.caList();
  }

  createCa(dto: CaRequestDto): Observable<void> {
    return this.endpoints.createCa(dto).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  getProfileList(): Observable<Profile[]> {
    if (this.useMockData) {
      return of(this.mockProfileList).pipe(delay(1000));
    }
    return this.endpoints.profileList();
  }

  deleteProfile(profileName: string): Observable<void> {
    return this.endpoints.deleteProfile({ profileName }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  deleteCA(ca: CA): Observable<void> {
    if (this.useMockData) {
      console.debug('deleteCA', ca);
      this.mockCertificationAuthorities =
        this.mockCertificationAuthorities.filter(a => a.caName !== ca.caName);
      return of(undefined).pipe(
        delay(1000),
        tap(() => console.debug('deleted')),
      );
    }
    if (!ca.caName) {
      return throwError(() => new Error('Empty caName'));
    }
    return this.endpoints.deleteCa({ caName: ca.caName }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  renewCA(ca: CA): Observable<void> {
    if (!ca.caName) {
      return throwError(() => new Error('Empty caName'));
    }
    return this.endpoints.renewCa({ caName: ca.caName }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  revokeCA(
    ca: CA,
    reason: CaRevokeReason,
    revokeLeafCertificates: boolean,
  ): Observable<void> {
    if (!ca.caName) {
      return throwError(() => new Error('Empty caName'));
    }
    return this.endpoints
      .revokeCa({ caName: ca.caName }, { reason, revokeLeafCertificates })
      .pipe(
        map(() => {
          /* map to void */
        }),
      );
  }

  generateCrlBlob(caName: string): Observable<Blob> {
    return this.endpoints.generateCrl({ caName });
  }

  generateCrlBase64(caName: string): Observable<string> {
    return this.generateCrlBlob(caName).pipe(switchMap(blobToBase64));
  }

  updateCa(caName: string, dto: UpdateCaRequestDto): Observable<CA> {
    if (this.useMockData) {
      const ca = this.mockCertificationAuthorities.find(
        item => item.caName === caName,
      );
      if (ca) {
        const editedCa: CA = {
          ...ca,
          defaultCRLDistPoint: dto.defaultCRLDistPoint,
          defaultOCSPServiceLocator: dto.defaultOCSPServiceLocator,
          useLdapDNOrder: dto.useLdapDNOrder,
        };
        this.mockCertificationAuthorities =
          this.mockCertificationAuthorities.map(item =>
            item.caName === editedCa.caName ? editedCa : item,
          );
        return of(editedCa).pipe(delay(1000));
      }
      return throwError(() => 'FAIL!');
    }
    return this.endpoints.updateCa({ caName }, dto);
  }

  cryptotokenSlots(): Observable<CryptotokenSlot[]> {
    if (this.useMockData) {
      return of([
        { name: 'foo', id: 0 },
        { name: 'bar', id: 1 },
        { name: 'baz', id: 2 },
      ]).pipe(
        delay(1000),
        tap(() => console.debug('Cryptotoken Slots fetched')),
      );
    }
    return this.endpoints.cryptotokenSlots();
  }

  getCryptotokenList(): Observable<CryptotokenDto[]> {
    if (this.useMockData) {
      return of(this.mockCryptoTokenList).pipe(delay(1000));
    }
    return this.endpoints.cryptotokenList();
  }

  newCryptotoken(dto: CreateCryptoTokenRequestDto): Observable<void> {
    if (this.useMockData) {
      return of(undefined).pipe(delay(1000));
    }
    return this.endpoints.newCryptoToken(dto).pipe(
      map(() => {
        /* return void */
      }),
    );
  }

  getCryptotokenKeys(tokenName: string): Observable<CryptotokenKey[]> {
    if (this.useMockData) {
      return of([
        {
          tokenName,
          details: {
            alias: 'foo',
            keyAlgorithm: 'algo',
            keySpecification: 'mock',
            subjectKeyID: 'myid',
            createdTime: new Date(1),
            updateTime: new Date(2),
          },
        },
      ]).pipe(delay(1000));
    }
    return this.endpoints
      .cryptotokenKeys({ tokenName })
      .pipe(
        map(dto =>
          dto.map(item => cryptotokenKeyDtoToCryptotokenKey(tokenName, item)),
        ),
      );
  }

  deleteCryptotoken(tokenName: string): Observable<void> {
    return this.endpoints.deleteCryptotoken({ tokenName }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  deleteCryptotokenKey(tokenName: string, alias: string): Observable<void> {
    return this.endpoints.deleteCryptotokenKey({ tokenName, alias }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  createProfile(dto: CreateCertificateProfileRequestDto) {
    return this.endpoints.createProfile(dto).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  updateProfile(
    profileName: string,
    dto: UpdateProfileRequestDto,
  ): Observable<Profile> {
    return this.endpoints.updateProfile({ profileName }, dto);
  }

  activateToken(tokenName: string, password: string): Observable<void> {
    return this.endpoints.activateToken({ tokenName }, { password }).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  deactivateToken(tokenName: string): Observable<void> {
    return this.endpoints.deactivateToken({ tokenName }, undefined).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  createCryptotokenKey(
    tokenName: string,
    dto: GenerateKeyRequestDto,
  ): Observable<void> {
    return this.endpoints.createCryptotokenKey({ tokenName }, dto).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  getTokenByCode(code: string): Observable<string> {
    if (this.useMockData) {
      return of(
        'eyJ0b2tlbiI6ImV5SnFhM1VpT2lKb2RIUndjem92TDJWdWRHVnljSEpwYzJVdWVHVnllR1Z6TG1OdmJXTmhjM1F1Ym1WMEwzaGxjbmhsY3kxemNDOXJaWGx6TDJwM2EzTWlMQ0pyYVdRaU9pSjRaWEo0WlhOemNGOXdjbTlrWHpJd01qTWlMQ0poYkdjaU9pSlNVekkxTmlKOS5leUp6ZFdJaU9pSmlhbTl6WlhBek5qUWlMQ0pzWVhOMFRtRnRaU0k2SWtwUFUwVlFTQ0lzSW1ScGMzQnNZWGxPWVcxbElqb2lTazlUUlZCSUxDQkNVa2xCVGlBb1EyOXVkSEpoWTNSdmNpa2lMQ0pwYzNNaU9pSm9kSFJ3Y3pvdkwyVnVkR1Z5Y0hKcGMyVXVlR1Z5ZUdWekxtTnZiV05oYzNRdWJtVjBMM2hsY25obGN5MXpjQ0lzSW1GMVpDSTZXeUpvZEhSd09pOHZiRzlqWVd4b2IzTjBPalF5TURBdmNHRnpjM0J2Y25SRGIyUmxJaXdpZUhCcmFTMTRZMkV0WkdWMklsMHNJblZwWkNJNkluaDRlSGw1ZVhwNmVDSXNJbVpwY25OMFRtRnRaU0k2SWtKU1NVRk9JaXdpYm1KbUlqb3hOelUyTXpBNE1ETTRMQ0poY0hCc2FXTmhkR2x2YmlJNmV5SjRjR3RwTFhoallTMWtaWFlpT2x0N0luQmhjblJ1WlhJaU9pSmpiMjFqWVhOMElpd2ljbTlzWlNJNklrTlBUVU5CVTFRdFdFTkJYMVZUUlZKZlVrOGlMQ0p5YVdkb2RITWlPbHNpVGtFaVhYMWRmU3dpY0dGeWRHNWxja2xFSWpvaVkyOXRZMkZ6ZENJc0ltVjRjQ0k2TVRjMU5qTTVORGN6T0N3aWFXRjBJam94TnpVMk16QTRNek00TENKcWRHa2lPaUo0WlhKNFpYTXRjM0F1TVRWa05URTVOVGt0WW1VNFlpMDBObUUzTFdGaE16VXRNbVkzT1dRM05UZzFNakptSWl3aVpXMWhhV3dpT2lKQ1VrbEJUbDlLVDFORlVFaEFZMjl0WTJGemRDNWpiMjBpZlEuWlFlc2wyM01PRDdqR1BVQmVUbTU1aUpTQ0JVNWxQaFI4Tm9nOUxCZm5ub3QySTJOY0pobEFmb1AtTGk5cjRveDJvdXplVXp0WndHc3pFaERXVGxBSjNZc3lxOWNlLThSVmpHbEx5THZmQ29aQUxZT2pUdUJ1LUo2Y2owY2ZSRkYtMHBtc0VkVjIwa1RBdzB4SkN6X0o3QVpaTy1XTVFET0F6MmRWbXBFUWdrZHdKM0x4MDRxQUQ5MUtMd2lHcXdIVC1ieklBbC1XSEJodWhFaXRHN2U5WFVUSzd1NGVZSUo5ZVI3amFrZWNNdDR6LWtqXzd2TWpzVlNVTXk4Vm1ydk1RRWppaWE3MDB1OWtDUnJTa0FsT1NONnVPYl9QVTJ6RTBiXzFmZnMwMEJOM1pxZmEwNGJ4ZDAxQjR6aGdyd2VpY2ZBVXJ4Wmh6QnVQM3Q4RHF5TzF3Iiwibm9uY2UiOm51bGwsInRpbWVzdGFtcCI6bnVsbCwidG9rZW5UeXBlIjoiUEFTU1BPUlQifQ==',
      );
    }
    return this.httpClient.post(
      this.codeEndpoint,
      { code },
      { responseType: 'text' },
    );
  }

  provisionOperationalCa(dto: CreateCaFullDto): Observable<void> {
    return this.endpoints.provisionOperationalCa(dto).pipe(
      map(() => {
        /* map to void */
      }),
    );
  }

  requestCsr(caName: string): Observable<string> {
    return this.endpoints.requestCsr({ caName });
  }

  importCertChain(caName: string, certChain: string): Observable<void> {
    return this.endpoints
      .importCertChain({ caName }, { externalCaChain: certChain })
      .pipe(
        map(() => {
          /* map to void */
        }),
      );
  }

  constructor() {
    this.store
      .select(UserSelector.selectUser)
      .pipe(
        tap(user => {
          this.token = user?.jwtToken ?? '';
        }),
      )
      .subscribe();
  }
}
